import React, { useState, useEffect, useContext } from 'react';
import FormControl from '@mui/material/FormControl';
import {
  MenuItem,
  Select,
  InputLabel,
  SelectChangeEvent,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Container,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import { createApiClient } from 'api/apiClient';
import { useQuery, useMutation } from 'react-query';
import { UserContext } from 'contexts/userContext';
import RPMDeviceOrdersHistory from 'pages/RPMDeviceOrdersHistory/RPMDeviceOrdersHistory';

function RPMDeviceOrders() {
  const [selectedOrgLength, setSelectedOrgLength] = useState(true);
  const [selectedDevicesLength, setSelectedDevicesLength] = useState(true);
  const [deviceNumTextLength, setDeviceNumTextLength] = useState(true);
  const [orderSuccess, setOrderSuccess] = useState<null | boolean>(null);
  const [hide, setHide] = useState<null | boolean>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmationData, setConfirmationData] = useState<{
    orgId: number;
    placedOrderData: {
      device_type: string;
      kit_id: string;
      device_provider_name: string;
      no_of_devices: string;
    };
  } | null>(null);

  const { userData } = useContext(UserContext);
  const accessToken = localStorage.getItem('access_token');
  const controller = new AbortController();

  const OrgID = userData?.userOrg;

  const apiClient = createApiClient(accessToken!, controller);

  const { isLoading, refetch, data } = useQuery('get-rpm-device-order', () =>
    apiClient.getRpmDeviceOrder(OrgID as number),
  );

  const PlaceOrder = withStyles({
    root: {
      margin: '10px',
    },
    label: {
      textTransform: 'capitalize',
    },
  })(Button);

  const [selectedOrg, setSelectedOrg] = useState('');
  const [selectedDevice, setSelectedDevice] = useState('');
  const [deviceNumText, setDeviceNumText] = useState('');

  const handleSelectORG = (event: SelectChangeEvent) => {
    setSelectedOrg(event.target.value);
    setSelectedDevice('');
  };

  const handleSelectTOD = (event: SelectChangeEvent) => {
    setSelectedDevice(event.target.value);
  };

  const noOfDeviceInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    let numberValue = parseInt(inputValue);
    if (isNaN(numberValue) || numberValue < 0) {
      numberValue = 0;
    }
    setDeviceNumText(numberValue.toString());
  };

  const { isLoading: isPlacingOrder, mutate: placeOrder } = useMutation(
    'place-order',
    ({
      orgId,
      placedOrderData,
    }: {
      orgId: number;
      placedOrderData: {
        device_type: string;
        kit_id: string;
        device_provider_name: string;
        no_of_devices: string;
      };
    }) => apiClient.postRpmDeviceOrder(orgId, placedOrderData),
    {
      onSuccess: response => {
        // Perform actions after successful order placement
        setSelectedOrg('');
        setSelectedDevice('');
        setDeviceNumText('');
        setOrderSuccess(true);
        setHide(true);
      },
      onError: error => {
        // Handle errors during order placement
        setSelectedOrg('');
        setSelectedDevice('');
        setDeviceNumText('');
        setOrderSuccess(false);
        setHide(true);
      },
    },
  );

  const handlePlaceOrder = () => {
    if (selectedOrg && selectedDevice && deviceNumText && data) {
      const selectedOrgData = data.find(
        item => item.organizationId === selectedOrg,
      );
      if (selectedOrgData) {
        const selectedDeviceObject = selectedOrgData.Device.find(
          device => device.kitId === selectedDevice,
        );
        if (selectedDeviceObject) {
          const { device_type, kitId } = selectedDeviceObject;
          const device_provider_name = selectedOrgData.device_provider_name;
          const no_of_devices = deviceNumText;
          const placedOrderData = {
            device_type,
            kit_id: kitId,
            device_provider_name,
            no_of_devices,
          };
          setConfirmationData({
            orgId: OrgID as number,
            placedOrderData,
          });
          setOpenDialog(true);
        }
      }
    }
  };

  const handleConfirmOrder = () => {
    if (confirmationData) {
      placeOrder(confirmationData);
      setOpenDialog(false);
    }
  };

  const handleCancelOrder = () => {
    setOpenDialog(false);
    setConfirmationData(null);
  };

  useEffect(() => {
    if (Number(deviceNumText) > 0) {
      setDeviceNumTextLength(false);
    }
    if (Number(deviceNumText) === 0) {
      setDeviceNumTextLength(true);
    }

    selectedOrg ? setSelectedOrgLength(false) : setSelectedOrgLength(true);

    selectedDevice
      ? setSelectedDevicesLength(false)
      : setSelectedDevicesLength(true);
  }, [deviceNumText, selectedOrg, selectedDevice]);

  useEffect(() => {
    if (hide !== null) {
      const timer = setTimeout(() => {
        setHide(false);
      }, 2700);
      return () => clearTimeout(timer);
    }
  }, [hide]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Grid container spacing={4} alignItems="flex-end" sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small" variant="outlined" sx={{ bgcolor: '#fff', borderRadius: 2 }}>
            <InputLabel shrink htmlFor="provider-org-select" sx={{ color: '#888', fontWeight: 400 }}>
              Device Provider Organization
            </InputLabel>
            <Select
              labelId="provider-org-label"
              id="provider-org-select"
              value={selectedOrg}
              onChange={handleSelectORG}
              label="Device Provider Organization"
              displayEmpty
              sx={{ borderRadius: 2, minHeight: 48, '.MuiSelect-select': { py: 1.5 } }}
            >
              {data && data?.filter((item:any) => item?.device_provider_name === "BodyTrace")?.map((item:any, index:any) => (
                <MenuItem key={index} value={item.organizationId}>
                  {item.device_provider_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small" variant="outlined" sx={{ bgcolor: '#fff', borderRadius: 2 }}>
            <InputLabel shrink htmlFor="device-type-select" sx={{ color: '#888', fontWeight: 400 }}>
              Type of Device
            </InputLabel>
            <Select
              labelId="device-type-label"
              id="device-type-select"
              value={selectedDevice}
              onChange={handleSelectTOD}
              label="Type of Device"
              renderValue={selected => {
                if (!selected) return <span style={{ color: '#bbb' }}>Select Provider</span>;
                const found = data?.find((item:any) => item.organizationId === selectedOrg)?.Device.find((device:any) => device.kitId === selected);
                return found ? found.device_type : selected;
              }}
              displayEmpty
              disabled={!selectedOrg}
              sx={{ borderRadius: 2, minHeight: 48, '.MuiSelect-select': { py: 1.5 } }}
            >
              {data && data.find((item:any) => item.organizationId === selectedOrg)?.Device.map((device:any, index:any) => (
                <MenuItem key={index} value={device.kitId}>
                  {device.device_type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Number of Device"
            variant="outlined"
            onChange={noOfDeviceInput}
            value={deviceNumText}
            disabled={!selectedDevice}
            sx={{ borderRadius: 2, bgcolor: '#fff', minHeight: 48, '& .MuiInputBase-input': { py: 1.5, color: '#222' }, '& .MuiInputLabel-root': { color: '#888', fontWeight: 400 } }}
            inputProps={{ min: 1, style: { color: '#222' } }}
            placeholder="Number of Device"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={deviceNumTextLength || selectedDevicesLength || selectedOrgLength}
            onClick={handlePlaceOrder}
            sx={{ height: 48, fontWeight: 700, fontSize: 16, bgcolor: '#90c2ee', color: '#fff', borderRadius: 2, boxShadow: 'none', '&:hover': { bgcolor: '#64b5f6' } }}
          >
            Place Order
          </Button>
        </Grid>
      </Grid>
      <div
        style={{
          margin: 'auto',
          width: '47%',
          padding: 47,
        }}
      >
        {hide ? (
          <>
            {orderSuccess === true && (
              <Alert severity="success">ORDER PLACED SUCCESSFULLY</Alert>
            )}
            {orderSuccess === false && (
              <Alert severity="error">PLEASE TRY AGAIN AFTER SOMETIME</Alert>
            )}
          </>
        ) : null}
      </div>
      <Dialog open={openDialog} onClose={handleCancelOrder}>
        <DialogTitle>Confirm Order</DialogTitle>
        <DialogContent>
          Are you sure you want to place this order?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelOrder} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmOrder} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <RPMDeviceOrdersHistory />
    </Container>
  );
}

export default RPMDeviceOrders;
